import React, { useCallback, useState } from 'react';
import { Button } from 'components/ui/button';
import { Col, Dropdown, Row } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { deleteNote, updateNote } from '../../../helpers/prospect_api';
import { faEllipsisVertical, faPencil, faTrash } from '@fortawesome/pro-solid-svg-icons';
import UnstyledDropdownToggle from 'components/bootstrap_extensions/bs5/unstyled_dropdown_toggle';
import TrixEditor from 'components/ui/trix/trix_editor';
import { displayToast } from '../../../helpers/display_toast';
import type { Note } from 'bundles/connect/types';

type Props = {
  note: Note;
  onDelete: (note: Note) => void;
  onUpdate: (note: Note) => void;
}

export default function NoteCard({ note, onDelete, onUpdate }: Props) {
  const [body, setBody] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const handleDelete = useCallback(() => {
    deleteNote(note.id).then(() => {
      onDelete(note);
      displayToast('Note successfully deleted', 'success');
    }).catch(() => {
      displayToast('Something went wrong', 'error');
    });
  }, [note.id]);

  const handleUpdate = useCallback(() => {
    setSaving(true);
    updateNote(note.id, body).then((updatedNote) => {
      onUpdate(updatedNote);
      setBody('');
      displayToast('Note successfully updated', 'success');
    }).finally(() => setSaving(false))
      .catch(() => displayToast('Something went wrong', 'error'));
  }, [body]);

  const handleStartEdit = useCallback(() => {
    setBody(note.body);
  }, [note]);

  const createdAt = new Date(note.createdAt).toLocaleDateString();
  const updatedAt = new Date(note.updatedAt).toLocaleDateString();

  return (
    <>
      {body !== '' && (
        <div className="d-flex flex-grow-1 flex-column gap-2">
          <div className="privacy-mask">
            <TrixEditor id="new-note" initialValue={body} name="body" onChange={setBody} />
          </div>

          <Row className="justify-content-end">
            <Col xs="auto">
              <Button disabled={saving} label="Cancel" variant="ghost" onClick={() => setBody('')} />
            </Col>
            <Col xs="auto">
              <Button disabled={saving} label="Save" variant="secondary" onClick={handleUpdate} />
            </Col>
          </Row>
        </div>
      )}
      {body === '' && (
        <>
          <Col className="d-flex flex-column gap-2">

            {body === '' && (
              <div className="privacy-mask" dangerouslySetInnerHTML={{ __html: note.body }} />
            )}
            <small className="text-muted">
              Created on {createdAt}
              {createdAt !== updatedAt && (
                <span> • Updated on {updatedAt}</span>
              )}
            </small>
          </Col>
          <Col xs="auto">
            <Dropdown>
              <Dropdown.Toggle as={UnstyledDropdownToggle} className="text-muted">
                <FontAwesomeIcon className="clickable px-2" icon={faEllipsisVertical} />
              </Dropdown.Toggle>
              <Dropdown.Menu className="py-0">
                <Dropdown.Item className="border-top-0" onClick={handleStartEdit}>
                  <FontAwesomeIcon className="me-2" fixedWidth icon={faPencil} />
                  Edit
                </Dropdown.Item>
                <Dropdown.Item className="border-bottom-0 text-danger" onClick={handleDelete}>
                  <FontAwesomeIcon className="me-2" fixedWidth icon={faTrash} />
                  Delete
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </>
      )}
    </>
  );
}
